import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

type ImportStep = "upload" | "mapping" | "preview" | "importing" | "done";

interface ParsedRow {
  [key: string]: string;
}

const CRM_FIELDS: readonly { key: string; label: string; required?: boolean }[] = [
  { key: "email", label: "Email", required: true },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "phone", label: "Phone" },
  { key: "source", label: "Source" },
  { key: "tags", label: "Tags (comma-separated)" },
];

interface BulkImportProps {
  onClose: () => void;
  onComplete: () => void;
}

/** CSV import wizard: upload → field mapping → preview → import. */
export function BulkImport({ onClose, onComplete }: BulkImportProps) {
  const [step, setStep] = useState<ImportStep>("upload");
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<ParsedRow[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  // Step 1: Parse CSV file
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split("\n").filter((line) => line.trim());
      if (lines.length < 2) return;

      const headerLine = lines[0];
      if (!headerLine) return;
      const headers = parseCSVLine(headerLine);
      const rows = lines.slice(1).map((line) => {
        const values = parseCSVLine(line);
        const row: ParsedRow = {};
        headers.forEach((h, i) => { row[h] = values[i] ?? ""; });
        return row;
      });

      setCsvHeaders(headers);
      setCsvRows(rows);

      // Auto-map obvious fields
      const autoMap: Record<string, string> = {};
      for (const field of CRM_FIELDS) {
        const match = headers.find((h) =>
          h.toLowerCase().replace(/[_\s-]/g, "") === field.key.toLowerCase()
          || h.toLowerCase().includes(field.key.toLowerCase()),
        );
        if (match) autoMap[field.key] = match;
      }
      setFieldMapping(autoMap);
      setStep("mapping");
    };
    reader.readAsText(file);
  }, []);

  // Step 3: Import contacts
  const handleImport = useCallback(async () => {
    setStep("importing");
    setImportTotal(csvRows.length);
    const errors: string[] = [];
    const BATCH_SIZE = 10;

    for (let i = 0; i < csvRows.length; i += BATCH_SIZE) {
      const batch = csvRows.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (row, batchIdx) => {
        const contact: Record<string, unknown> = {};
        for (const [crmField, csvHeader] of Object.entries(fieldMapping)) {
          const value = row[csvHeader]?.trim();
          if (!value) continue;
          if (crmField === "tags") {
            contact[crmField] = value.split(",").map((t) => t.trim()).filter(Boolean);
          } else {
            contact[crmField] = value;
          }
        }

        if (!contact.email) {
          errors.push(`Row ${i + batchIdx + 2}: Missing email`);
          return;
        }

        try {
          await apiFetch("/crm/people", {
            method: "POST",
            body: JSON.stringify(contact),
          });
        } catch (err) {
          errors.push(`Row ${i + batchIdx + 2}: ${err instanceof Error ? err.message : "Failed"}`);
        }
      });

      await Promise.all(promises);
      setImportProgress(Math.min(i + BATCH_SIZE, csvRows.length));
    }

    setImportErrors(errors);
    setStep("done");
  }, [csvRows, fieldMapping]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-lg bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Import Contacts</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        {/* Step indicator */}
        <div className="mb-6 flex gap-2">
          {(["upload", "mapping", "preview", "importing"] as const).map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${
              (["upload", "mapping", "preview", "importing", "done"] as const).indexOf(step) >= i
                ? "bg-primary" : "bg-muted"
            }`} />
          ))}
        </div>

        {step === "upload" && (
          <div className="flex flex-col items-center gap-4 py-8">
            <p className="text-4xl">📄</p>
            <p className="text-sm text-muted-foreground">Upload a CSV file with contact data</p>
            <label className="cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              Choose File
              <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        )}

        {step === "mapping" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Map your CSV columns to contact fields. {csvRows.length} rows found.
            </p>
            {CRM_FIELDS.map((field) => (
              <div key={field.key} className="flex items-center gap-3">
                <label className="w-36 text-sm font-medium">
                  {field.label}{field.required ? " *" : ""}
                </label>
                <select
                  value={fieldMapping[field.key] ?? ""}
                  onChange={(e) => setFieldMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                >
                  <option value="">— Skip —</option>
                  {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setStep("upload")} className="rounded-md border border-border px-4 py-2 text-sm">Back</button>
              <button
                onClick={() => setStep("preview")}
                disabled={!fieldMapping.email}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                Next: Preview
              </button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Preview first 5 rows. {csvRows.length} total will be imported.
            </p>
            <div className="overflow-x-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    {CRM_FIELDS.filter((f) => fieldMapping[f.key]).map((f) => (
                      <th key={f.key} className="px-3 py-2 text-left">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {CRM_FIELDS.filter((f) => fieldMapping[f.key]).map((f) => (
                        <td key={f.key} className="px-3 py-1.5">{row[fieldMapping[f.key] ?? ""] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setStep("mapping")} className="rounded-md border border-border px-4 py-2 text-sm">Back</button>
              <button onClick={handleImport} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                Import {csvRows.length} Contacts
              </button>
            </div>
          </div>
        )}

        {step === "importing" && (
          <div className="space-y-4 py-8 text-center">
            <p className="text-sm">Importing contacts...</p>
            <div className="mx-auto h-2 w-64 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${importTotal > 0 ? (importProgress / importTotal) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{importProgress} / {importTotal}</p>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-4 py-8 text-center">
            <p className="text-4xl">{importErrors.length === 0 ? "✅" : "⚠️"}</p>
            <p className="text-sm font-medium">
              {importProgress - importErrors.length} contacts imported successfully
            </p>
            {importErrors.length > 0 && (
              <div className="mx-auto max-h-32 max-w-sm overflow-y-auto rounded border border-destructive/20 bg-destructive/5 p-3 text-left text-xs text-destructive">
                {importErrors.map((err, i) => <p key={i}>{err}</p>)}
              </div>
            )}
            <button
              onClick={() => { onComplete(); onClose(); }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Simple CSV line parser — handles quoted fields with commas. */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}
